import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import Title from '../../commonComponents/Title';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import TopicBanner from '../../commonComponents/pageLayout/header/TopicBanner';
import { compose, displayUserName } from '../../services/utilityFunctions';
import translate from '../../commonComponents/translate';
import UserProp from '../../commonComponents/propTypes/userProp';
import FacebookIcon from '../shared/icons/FacebookIcon';
import MetaContext from '../../contextProviders/MetaContext';
import InstagramIcon from '../shared/icons/InstagramIcon';
import LinkedInIcon from '../shared/icons/LinkedInIcon';
import TwitterIcon from '../shared/icons/TwitterIcon';
import PhoneIcon from '../shared/icons/PhoneIcon';
import DividerDashSeparator from '../shared/icons/DividerDashSeparator';
import EnrollForm from '../../commonComponents/forms/EnrollForm';
import LoginModal from '../signIn/modals/LoginModal';
import { useModal } from '../../services/customHooks';
import TenantProp from '../../commonComponents/propTypes/tenantProp';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import Notifications from '../../services/notifications';
import BasicModal from '../../commonComponents/modal/BasicModal';
import CurrencyProp from '../profile/propTypes/currencyProp';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';

function MarketUserPage({
  t,
  user,
  marketPlaceIsPublic,
  languages,
  tenant,
  organizationIdentifier,
  availableCurrencies,
  availableCountries,
}) {
  const { loggedUser } = useContext(LoggedUserContext);
  const countries = availableCountries.map(country => ({
    id: country.code,
    text: country.name,
  }));
  const currencies = availableCurrencies.map(currency => ({
    id: currency.code,
    text: currency.name,
  }));
  const { branding } = useContext(MetaContext);

  const { configured: isCalendlyConfigured, config: calendlyConfig } =
    user?.connectors?.calendly || {};
  const { url: calendlyAppointmentURL } = calendlyConfig || {};

  const [showModal, setShowModal] = useModal({ show: false, data: null });
  const handleModal = useCallback(show => {
    setShowModal({ show, data: null });
  }, []);

  // State to show the iframe
  const [showIframe, setShowIframe] = useModal({ show: false, data: null });

  const browserHistory = useHistory();

  const imageURL = user?.image?.url || '/images/no_user.png';
  /** Handle functions */
  // Mail to User
  const mailShareUrl = `mailto:${user.email}`;

  const handleAppointment = useCallback(() => {
    if (!isCalendlyConfigured || !calendlyAppointmentURL) {
      window.location.href = mailShareUrl;
      return;
    }
    setShowIframe({ show: true, data: calendlyAppointmentURL });
  }, [
    setShowIframe,
    mailShareUrl,
    isCalendlyConfigured,
    calendlyAppointmentURL,
  ]);

  const handleCloseIframe = useCallback(() => {
    setShowIframe({ show: false, data: null });
  }, [showIframe]);

  // Handle the different social pages
  const renderSocial = (type, social) => {
    let icon;
    switch (type) {
      case 'linkedIn':
        icon = <LinkedInIcon color={branding.primaryColor} />;
        break;
      case 'facebook':
        icon = <FacebookIcon color={branding.primaryColor} />;
        break;
      case 'instagram':
        icon = <InstagramIcon color={branding.primaryColor} />;
        break;
      case 'twitter':
      default:
        icon = <TwitterIcon color={branding.primaryColor} />;
        break;
    }

    return (
      <a
        target="_blank"
        rel="noopener noreferrer"
        href={social}
        data-testid={`${type}-social`}
      >
        {icon}
      </a>
    );
  };

  const onCompleteSignIn = useCallback(
    (isLogin = false) => {
      if (!isLogin) {
        Notifications.showNotificationSuccess(
          t('success'),
          t('user.successfully.registered'),
        );
      }
      if (!isLogin && !loggedUser) {
        window.location.href = '/dashboard';
      } else {
        window.location.reload();
      }
    },
    [t],
  );

  return (
    <>
      {showIframe?.show && (
        <BasicModal
          onClose={handleCloseIframe}
          id="calendlyModal"
          saveMessage={t('close')}
          ignoreCancelBtn
        >
          <div className="open-in-new-tab-container">
            <a href={calendlyAppointmentURL} target="_blank" rel="noreferrer">
              {t('open.in.new.tab')}
            </a>
          </div>
          <iframe
            src={calendlyAppointmentURL}
            title="Calendly"
            style={{
              width: '100%',
              minHeight: '550px',
            }}
          />
        </BasicModal>
      )}
      {showModal.show && (
        <LoginModal
          onClose={() => handleModal(false)}
          languages={[]}
          showRegisterAccount={false}
          logo={null}
          redirectLink={browserHistory.location.pathname}
          onLoggedIn={() => onCompleteSignIn(true)}
          ignoreRedirection
        />
      )}
      <Title
        title={`${t('market.userPage.title')} - ${user?.userPageName || ''}`}
      >
        <ContentContainer>
          <TopicBanner />
          <div id="marketUserPage">
            <div className="m-content m-portlet market-full-container">
              <div className="full-page-container-with-subheader market-content-container">
                <div className="row">
                  <div className="col-lg-4 col-12">
                    <div className="market-user-image-info">
                      <div className="market-user-image">
                        <img src={imageURL} alt={user?.userPageName || ''} />
                      </div>
                      <div className="market-user-contacts">
                        <h5 className="text-header">
                          {t('market.userPage.contact')}
                        </h5>
                        <DividerDashSeparator />
                        <div className="row">
                          <div className="col-12">
                            <div className="row market-user-contacts-phone">
                              <div className="col-1">
                                <PhoneIcon color={branding.primaryColor} />
                              </div>
                              <p className="col-11">{user.phone}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-8 col-12">
                    <div className="row market-user-name">
                      <div className="col-12">
                        <h2>{user.username || displayUserName(user)}</h2>
                      </div>
                      <div className="col-12 userPage-container">
                        <span>{user?.userPageName || ''}</span>
                      </div>
                      <div className="col-12">
                        <DividerDashSeparator color="#ffffff" />
                      </div>
                    </div>
                    <div className="row market-about-section mb-2 pb-2">
                      {user?.softSkills?.biography?.length > 0 && (
                        <div className="col-12">
                          <h4 className="text-header">
                            {t('market.userPage.about')}
                          </h4>
                          <DividerDashSeparator />
                          <div>
                            <p>{user?.softSkills?.biography || ''}</p>
                          </div>
                        </div>
                      )}
                      <div className="col-lg-10 col-12 market-button-container">
                        <div className="row">
                          <div className="col-lg-5 col-12">
                            <button
                              type="button"
                              className=" btn m-btn--pill btn-dynamic"
                              onClick={handleAppointment}
                            >
                              {t('market.userPage.makeAppointment')}
                            </button>
                          </div>
                          <div className="col-lg-5 col-12">
                            {marketPlaceIsPublic && (
                              <a
                                className="btn btn-outline-dynamic m-btn m-btn--pill"
                                href="/shop"
                              >
                                {t('market.userPage.goToShop')}
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                      {/** SOCIAL MEDIA */}
                      <div className="col-12 market-socialMedia">
                        {(user?.socialMedia?.facebook ||
                          user?.socialMedia?.linkedIn ||
                          user?.socialMedia?.instagram ||
                          user?.socialMedia?.twitter) && (
                          <>
                            <h4 className="text-header">
                              {t('market.userPage.social')}
                            </h4>
                            <DividerDashSeparator />
                            <div className="row market-socialMedia-icons">
                              {user?.socialMedia?.facebook && (
                                <div className="col-lg-1 col-3 social-icon">
                                  {renderSocial(
                                    'facebook',
                                    user?.socialMedia?.facebook,
                                  )}
                                </div>
                              )}
                              {user?.socialMedia?.linkedIn && (
                                <div className="col-lg-1 col-3 social-icon">
                                  {renderSocial(
                                    'linkedIn',
                                    user?.socialMedia?.linkedIn,
                                  )}
                                </div>
                              )}
                              {user?.socialMedia?.instagram && (
                                <div className="col-lg-1 col-3 social-icon">
                                  {renderSocial(
                                    'instagram',
                                    user?.socialMedia?.instagram,
                                  )}
                                </div>
                              )}
                              {user?.socialMedia?.twitter && (
                                <div className="col-lg-1 col-3 social-icon">
                                  {renderSocial(
                                    'twitter',
                                    user?.socialMedia?.twitter,
                                  )}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <EnrollForm
                      languages={languages}
                      isPublic
                      onComplete={() => onCompleteSignIn(false)}
                      onLoginModalOpen={handleModal}
                      tenant={tenant}
                      organizationIdentifier={organizationIdentifier}
                      title={t('user.public.create.or.login')}
                      buttonTxt={t('login')}
                      loginTxt={t('login_createAccount')}
                      sponsoredBy={user._id}
                      countries={countries}
                      currencies={currencies}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ContentContainer>
      </Title>
    </>
  );
}

MarketUserPage.propTypes = {
  t: PropTypes.func.isRequired,
  user: PropTypes.shape(UserProp.propType).isRequired,
  marketPlaceIsPublic: PropTypes.bool.isRequired,
  tenant: PropTypes.shape(TenantProp.propType).isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  availableCountries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  availableCurrencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  organizationIdentifier: PropTypes.string.isRequired,
};

export default compose(translate)(MarketUserPage);
